import {
  Body, Controller, Delete, ForbiddenException, Get,
  HttpCode, Param, Patch, Post, Put, Query, Req, UseGuards,
} from '@nestjs/common';
import { GuardedRequest, AuthGuard, AuthenticatedRequest } from '../auth/auth.guard';
import { DemoModeGuard } from '../auth/demo-mode.guard';
import { StorefrontTemplatesService } from './storefront-templates.service';
import {
  ApplyStorefrontTemplateRequest,
  CreateStorefrontTemplateRequest,
  UpdateStorefrontTemplateRequest,
} from '@cart/shared-types';

const PLATFORM_ROLES = ['admin', 'super_admin', 'platform_admin'];

/**
 * Storefront *layout* templates — never to be confused with
 * `TemplatesController` (`api/src/app/templates/`), which is the EMAIL system
 * surfaced in admin under Settings → Templates. Every route here is prefixed
 * `storefront-templates` for exactly that reason.
 *
 * Structurally a clone of `ThemesController`. There is no unguarded companion
 * controller: the storefront reads its template from `GET /store/config`
 * (`tenancy/store-info.controller.ts`) together with the theme, rather than
 * from a dedicated endpoint of its own.
 */
@Controller()
@UseGuards(AuthGuard, DemoModeGuard)
export class StorefrontTemplatesController {
  constructor(private readonly templatesService: StorefrontTemplatesService) {}

  // ── Platform: template library management ─────────────────────────────────

  @Get('platform/storefront-templates')
  listAll(@Req() req: GuardedRequest) {
    this.requirePlatformRole(req);
    return this.templatesService.listAll();
  }

  @Get('platform/storefront-templates/:id')
  getById(@Param('id') id: string, @Req() req: AuthenticatedRequest) {
    this.requirePlatformRole(req);
    return this.templatesService.getById(id);
  }

  @Post('platform/storefront-templates')
  create(@Body() dto: CreateStorefrontTemplateRequest, @Req() req: AuthenticatedRequest) {
    this.requirePlatformRole(req);
    return this.templatesService.create(dto, req.user.id);
  }

  @Put('platform/storefront-templates/:id')
  update(
    @Param('id') id: string,
    @Body() dto: UpdateStorefrontTemplateRequest,
    @Req() req: AuthenticatedRequest,
  ) {
    this.requirePlatformRole(req);
    return this.templatesService.update(id, dto, req.user.id);
  }

  @Patch('platform/storefront-templates/:id/publish')
  publish(@Param('id') id: string, @Req() req: AuthenticatedRequest) {
    this.requirePlatformRole(req);
    return this.templatesService.setPublished(id, true, req.user.id);
  }

  @Patch('platform/storefront-templates/:id/unpublish')
  unpublish(@Param('id') id: string, @Req() req: AuthenticatedRequest) {
    this.requirePlatformRole(req);
    return this.templatesService.setPublished(id, false, req.user.id);
  }

  @Delete('platform/storefront-templates/:id')
  @HttpCode(200)
  remove(@Param('id') id: string, @Req() req: AuthenticatedRequest) {
    this.requirePlatformRole(req);
    return this.templatesService.remove(id, req.user.id);
  }

  // ── Merchant: browse published templates ──────────────────────────────────

  /**
   * `storeId` is optional: the picker passes the store it is open on so
   * `isLocked` is judged against that store's organisation. Without it the
   * actor's own organisation is used.
   */
  @Get('storefront-templates')
  listForMerchant(@Req() req: GuardedRequest, @Query('storeId') storeId?: string) {
    return this.templatesService.listForMerchant(req.user.id, storeId);
  }

  // ── Merchant: get / apply template on their store ─────────────────────────

  @Get('merchant/stores/:storeId/storefront-template')
  getStoreTemplate(@Param('storeId') storeId: string) {
    return this.templatesService.getStoreTemplate(storeId);
  }

  @Patch('merchant/stores/:storeId/storefront-template')
  applyTemplate(
    @Param('storeId') storeId: string,
    @Body() dto: ApplyStorefrontTemplateRequest,
    // Class-level @UseGuards(AuthGuard, DemoModeGuard) guarantees `user`.
    @Req() req: GuardedRequest,
  ) {
    return this.templatesService.applyToStore(
      storeId,
      dto.storefrontTemplateId,
      req.user.id,
      req.user.role,
    );
  }

  /** Asserts rather than returns — see the identical helper in ThemesController. */
  private requirePlatformRole(req: AuthenticatedRequest): asserts req is GuardedRequest {
    if (!req.user || !PLATFORM_ROLES.includes(req.user.role)) {
      throw new ForbiddenException('Platform admin access required');
    }
  }
}
